import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { DEFAULT_LOCALE, HEADER_REQUEST_ID } from "@lib/constants";
import ERROR_CODES from "@lib/error-codes";
import { MIXPANEL_EVENTS } from "@lib/mixpanel/mixpanel.event-list";
import mixpanelService from "@lib/mixpanel/mixpanel.service";
import axios, { AxiosHeaders } from "axios";
import axiosRetry from "axios-retry";
import moment from "moment";
import { v4 as uuid } from 'uuid';
import { permissionsManager } from "@lib/permissions/permissions-manager";
const DefaultManagerProps = {
  allowAuthorizeInterceptor: true
};
// Retry configuration for axios-retry
const RETRY_CONFIG = {
  retries: 3,
  retryDelay: retryCount => {
    return retryCount * 250; // 0.25 seconds base delay
  },
  retryCondition: error => {
    // Only retry on network errors
    return axiosRetry.isNetworkError(error) || error.code === 'ERR_NETWORK';
  }
};
class MuscleBackendManager {
  constructor(baseURL) {
    let config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DefaultManagerProps;
    this.allowUnauthenticatedUsers = permissionsManager.allowUnauthenticatedUsers() || process.env.NEXT_PUBLIC_LOGIN_MODE === "token_param";
    this.userIp = null;
    this.axiosInstance = void 0;
    this.baseURL = void 0;
    this.baseURL = baseURL;
    this.axiosInstance = this.createAxiosInstance(baseURL, config);
  }
  createAxiosInstance(baseURL, config) {
    let axiosInstance = axios.create({
      headers: {
        "Content-Type": "application/json"
      }
    });

    // Set axios instance attributes
    axiosInstance.defaults.baseURL = baseURL;

    // Configure axios-retry
    axiosRetry(axiosInstance, _objectSpread(_objectSpread({}, RETRY_CONFIG), {}, {
      shouldResetTimeout: true,
      onRetry: (retryCount, error) => {
        var _error$config, _error$config2, _error$config3;
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.API_RETRY_NETWORK_ERROR, {
          retry_attempt: retryCount,
          max_retries: RETRY_CONFIG.retries,
          calling_to: error === null || error === void 0 || (_error$config = error.config) === null || _error$config === void 0 ? void 0 : _error$config.url,
          method: error === null || error === void 0 || (_error$config2 = error.config) === null || _error$config2 === void 0 ? void 0 : _error$config2.method,
          base_url: error === null || error === void 0 || (_error$config3 = error.config) === null || _error$config3 === void 0 ? void 0 : _error$config3.baseURL
        });
      },
      onMaxRetryTimesExceeded: error => {
        var _error$config4, _error$config5, _error$config6;
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.MAX_RETRIES_REACHED, {
          max_retries: RETRY_CONFIG.retries,
          calling_to: error === null || error === void 0 || (_error$config4 = error.config) === null || _error$config4 === void 0 ? void 0 : _error$config4.url,
          method: error === null || error === void 0 || (_error$config5 = error.config) === null || _error$config5 === void 0 ? void 0 : _error$config5.method,
          base_url: error === null || error === void 0 || (_error$config6 = error.config) === null || _error$config6 === void 0 ? void 0 : _error$config6.baseURL,
          error_code: error.code,
          error_message: error.message
        });
      }
    }));
    axiosInstance.interceptors.request.use(config => {
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }
      config.headers.set(HEADER_REQUEST_ID, uuid());
      return config;
    });
    axiosInstance.interceptors.response.use(config => config, async error => {
      var _error$config7, _error$config8, _error$config9;
      const errorData = {
        error: error,
        calling_to: error === null || error === void 0 || (_error$config7 = error.config) === null || _error$config7 === void 0 ? void 0 : _error$config7.url,
        method: error === null || error === void 0 || (_error$config8 = error.config) === null || _error$config8 === void 0 ? void 0 : _error$config8.method,
        base_url: error === null || error === void 0 || (_error$config9 = error.config) === null || _error$config9 === void 0 ? void 0 : _error$config9.baseURL,
        from: 'axios-interceptor'
      };
      try {
        var _error$response, _error$response2, _error$response3;
        if (((_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 401 && config.allowAuthorizeInterceptor) {
          // Check if has access token previously
          const accessToken = this.getToken();
          if (accessToken && accessToken !== "no_auth") {
            var _error$config0;
            // If has access token, track the event, this means the user has been logged in
            mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.APPLICATION_SESSION_EXPIRED, {
              calling_to: error === null || error === void 0 || (_error$config0 = error.config) === null || _error$config0 === void 0 ? void 0 : _error$config0.url
            });
          }
          // If the user is not authorized, redirect to the home page
          if (typeof window !== "undefined") {
            localStorage.removeItem("access-token");
            localStorage.removeItem("guest-access-token");
            window.location.href = "/";
          }
        }
        const axiosError = error;
        // If no structured error is returned, create one
        if (!("code" in ((_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.data)) && !("error" in ((_error$response3 = error.response) === null || _error$response3 === void 0 ? void 0 : _error$response3.data))) {
          if ((error === null || error === void 0 ? void 0 : error.code) === "ERR_CANCELED") {
            mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.NO_STRUCTURED_INTERRUPTION_API_RESPONSE, errorData);
          }
          error.response.data = this.getNotHandledErrorData(error);
        }
        // If the error comes in ruby api format, format it to the shared format
        else if ("detail" in error.response.data) {
          var _error$response$data, _axiosError$response;
          mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.RUBY_API_INTERRUPTION, errorData);
          error.response.data = _objectSpread(_objectSpread({}, error.response.data), {}, {
            error: error.response.data.title,
            message: error.response.data.detail,
            code: error.response.data.code,
            hide_retry: (_error$response$data = error.response.data) === null || _error$response$data === void 0 ? void 0 : _error$response$data.hide_retry,
            _status: (_axiosError$response = axiosError.response) === null || _axiosError$response === void 0 ? void 0 : _axiosError$response.status
          });
        } else if ("error" in error.response.data) {
          var _error$response$data$, _error$response$data2, _error$response$data$2, _error$response$data3, _error$response$data4, _error$response$data5, _axiosError$response2;
          mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.JS_API_INTERRUPTION, errorData);
          error.response.data = _objectSpread(_objectSpread({}, error.response.data), {}, {
            error: error.response.data.error,
            message: (_error$response$data$ = (_error$response$data2 = error.response.data) === null || _error$response$data2 === void 0 ? void 0 : _error$response$data2.message) !== null && _error$response$data$ !== void 0 ? _error$response$data$ : error.response.data.error,
            code: (_error$response$data$2 = (_error$response$data3 = error.response.data) === null || _error$response$data3 === void 0 ? void 0 : _error$response$data3.code) !== null && _error$response$data$2 !== void 0 ? _error$response$data$2 : error.response.data.error,
            hide_retry: (_error$response$data4 = error.response.data) === null || _error$response$data4 === void 0 ? void 0 : _error$response$data4.hide_retry,
            type: (_error$response$data5 = error.response.data) === null || _error$response$data5 === void 0 ? void 0 : _error$response$data5.type,
            _status: (_axiosError$response2 = axiosError.response) === null || _axiosError$response2 === void 0 ? void 0 : _axiosError$response2.status
          });
        }
      } catch (e) {
        // Anything except ERR_CANCELED
        if ((error === null || error === void 0 ? void 0 : error.code) !== "ERR_CANCELED") {
          mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.API_THROW_INTERRUPTION, _objectSpread(_objectSpread({}, errorData), {}, {
            exception: e
          }));
        }
        return {
          data: this.getNotHandledErrorData(error)
        };
      }
      return error.response;
    });
    return axiosInstance;
  }
  getNotHandledErrorData(error) {
    var _error$response7;
    if ((error === null || error === void 0 ? void 0 : error.code) !== "ERR_CANCELED") {
      var _error$config1, _error$config10, _error$config11, _error$response4, _error$response5, _error$response6;
      mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.LOG_UNKNOWN_INTERRUPTION, {
        exception: error,
        calling_to: error === null || error === void 0 || (_error$config1 = error.config) === null || _error$config1 === void 0 ? void 0 : _error$config1.url,
        method: error === null || error === void 0 || (_error$config10 = error.config) === null || _error$config10 === void 0 ? void 0 : _error$config10.method,
        base_url: error === null || error === void 0 || (_error$config11 = error.config) === null || _error$config11 === void 0 ? void 0 : _error$config11.baseURL,
        from: 'get-not-handled-error-data',
        switch_case_log: {
          code: error === null || error === void 0 ? void 0 : error.code,
          status: error === null || error === void 0 || (_error$response4 = error.response) === null || _error$response4 === void 0 ? void 0 : _error$response4.status
        },
        will_enter_to_default_case: !(error !== null && error !== void 0 && (_error$response5 = error.response) !== null && _error$response5 !== void 0 && _error$response5.status) && (error === null || error === void 0 || (_error$response6 = error.response) === null || _error$response6 === void 0 ? void 0 : _error$response6.status) !== 401
      });
    }
    switch (error === null || error === void 0 ? void 0 : error.code) {
      case "ERR_CANCELED":
        return {
          error: "Request Canceled",
          message: "The request was canceled.",
          code: ERROR_CODES.Shared.REQUEST_CANCELED,
          _status: ERROR_CODES.Shared.REQUEST_TIMEOUT
        };
      case "ERR_NETWORK":
        return {
          error: "Network Error",
          message: "Network connection failed after retries. Please check your internet connection.",
          code: ERROR_CODES.Shared.NETWORK_ERROR,
          _status: "network_error"
        };
      case "ECONNABORTED":
        return {
          error: "Connection Timeout",
          message: "The connection was aborted.",
          code: ERROR_CODES.Shared.REQUEST_TIMEOUT
        };
      case "ECONNRESET":
        return {
          error: "Connection Reset",
          message: "The connection was reset.",
          code: ERROR_CODES.Shared.CONNECTION_RESET,
          _status: ERROR_CODES.Shared.CONNECTION_RESET
        };
      case "ERR_CONNECTION_REFUSED":
        return {
          error: "Connection Refused",
          message: "The connection was refused.",
          code: ERROR_CODES.Shared.CONNECTION_REFUSED,
          _status: ERROR_CODES.Shared.CONNECTION_REFUSED
        };
    }
    switch (error === null || error === void 0 || (_error$response7 = error.response) === null || _error$response7 === void 0 ? void 0 : _error$response7.status) {
      case 401:
        return {
          error: "Unauthorized",
          message: "User not authorized to perform this action.",
          code: ERROR_CODES.Shared.UNAUTHORIZED_ERROR,
          _status: 401
        };
      default:
        return {
          error: "Unknown Error",
          message: "An unknown error occurred. Please try again later.",
          code: ERROR_CODES.Shared.UNKNOWN_ERROR,
          _status: "unknown_error"
        };
    }
  }
  getToken() {
    if (typeof window === "undefined") {
      return "no_auth";
    }
    if (localStorage) {
      const accessToken = localStorage.getItem("access-token");
      const allowUnauthenticatedClients = permissionsManager.allowUnauthenticatedUsers();
      const guestToken = localStorage.getItem("guest-access-token");
      if (accessToken) return accessToken;
      if (guestToken && allowUnauthenticatedClients) return guestToken;
    }
    return "no_auth";
  }
  getAxiosInstance() {
    var _localStorage;
    let includeToken = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    if (includeToken) {
      this.axiosInstance.defaults.headers.common.Authorization = "Bearer ".concat(this.getToken());
      this.axiosInstance.defaults.headers.common["X-API-Key"] = "".concat(this.getToken());
      this.axiosInstance.defaults.headers.common["user-date-time"] = moment().format();
    }
    const hjId = (_localStorage = localStorage) === null || _localStorage === void 0 ? void 0 : _localStorage.getItem("hj__");
    if (hjId) {
      this.axiosInstance.defaults.headers.common["x-hjid"] = hjId;
    }

    // ADD NGROK HEADER
    this.axiosInstance.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';
    const locale = this.getLocale();
    this.axiosInstance.defaults.headers.common.lang = locale;
    this.axiosInstance.defaults.headers.common.language = locale;
    return this.axiosInstance;
  }
  getLocale() {
    var _localStorage2;
    if (typeof window === "undefined") return DEFAULT_LOCALE;
    return ((_localStorage2 = localStorage) === null || _localStorage2 === void 0 ? void 0 : _localStorage2.getItem("locale")) || DEFAULT_LOCALE;
  }
}
export default MuscleBackendManager;